import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { APP_URLS } from '../../appUrls';
import Button from '../../components/button/button';
import { IconEnum } from '../../components/icon';
import Spinner, { ClipSpinner } from '../../components/spinner';
import { ClientApplication } from '../../msal/clientApp';
import { SubscriptionOverviewDto, SubscriptionTypeEnum, SysKitSubscriptionsClient } from '../../services/service';
import { onExternalLinkClickCallback } from '../../utils/url';
import './controlCenter.scss';
import { NameControlCenterTile, PurchasedPlanControlCenterTile, StatusControlCenterTile, UsersControlCenterTile, ValidUntilControlCenterTile } from './controlCenterItems';

//#region Interfaces
interface ControlCenterProps /*extends RouteComponentProps*/ {
    subscriptionOverview?: SubscriptionOverviewDto
}

enum CopyLicenseKeyState {
    Idle = 'idle',
    Getting = 'getting',
    Copied = 'copied',
    Error = 'error',
}

//#endregion Interfaces

const controlCenter = ({ subscriptionOverview }: ControlCenterProps) => {
    const history = useHistory();
    const [copyLicenseKeyState, setCopyLicenseKeyState] = useState<CopyLicenseKeyState>(CopyLicenseKeyState.Idle);
    const copiedStateTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

    const onBuyNowClicked = () => {
        history.push(`/control-center/${subscriptionOverview?.id}/subscriptions`);
    };

    const onContactSupportClicked = () => {
        onExternalLinkClickCallback(APP_URLS.contactSupport);
    }

    const onOpenPointClickCallback = () => {
        onExternalLinkClickCallback(subscriptionOverview!.url);
    }

    const clearCopiedStateTimeout = () => {
        if (!copiedStateTimeout.current) {
            return;
        }

        clearTimeout(copiedStateTimeout.current);
        copiedStateTimeout.current = undefined;
    };

    useEffect(() => {
        setCopyLicenseKeyState(CopyLicenseKeyState.Idle);
        clearCopiedStateTimeout();
    }, [subscriptionOverview?.id]);

    useEffect(
        () => () => {
            clearCopiedStateTimeout();
        },
        []
    );

    const onCopyLicenseKeyClickCallback = async () => {
        if (!subscriptionOverview?.id) {
            return;
        }

        clearCopiedStateTimeout();
        setCopyLicenseKeyState(CopyLicenseKeyState.Getting);

        try {
            const client = new SysKitSubscriptionsClient();
            const licenseInfo = await client.getLicenseInfo(subscriptionOverview.id);
            if (!licenseInfo?.key) {
                throw new Error(`No license key found for subscription '${subscriptionOverview.id}'.`);
            }

            await navigator.clipboard.writeText(licenseInfo.key);
            setCopyLicenseKeyState(CopyLicenseKeyState.Copied);

            copiedStateTimeout.current = setTimeout(() => {
                setCopyLicenseKeyState(CopyLicenseKeyState.Idle);
                copiedStateTimeout.current = undefined;
            }, 5000);
        }
        catch (error) {
            console.error('Failed to copy license key to clipboard.', error);
            setCopyLicenseKeyState(CopyLicenseKeyState.Error);
            copiedStateTimeout.current = setTimeout(() => {
                setCopyLicenseKeyState(CopyLicenseKeyState.Idle);
                copiedStateTimeout.current = undefined;
            }, 5000);
        }
    }

    const onViewLicensesClicked = () => {
        const account = ClientApplication.getActiveAccountDetails();
        let viewLicensesUrl = subscriptionOverview!.url;
        if (account) {
            viewLicensesUrl = viewLicensesUrl + '/#/' + subscriptionOverview!.tenantId + '/settings/point-chargeable-users';
        }
        onExternalLinkClickCallback(viewLicensesUrl);
    }

    return (
        <div className="control-center control-center--container">
            <div className="control-center__main-content">
                {subscriptionOverview === undefined
                    ? <Spinner text='Loading...' style={{ height: 400, width: '80%', minWidth: '400px' }}>
                        <ClipSpinner size={70} color={'var(--electric-indigo)'} />
                    </Spinner>
                    : (<>
                        <div className="control-center__items">
                            <div className="control-center__layout">
                                <NameControlCenterTile
                                    name={subscriptionOverview.name}
                                    url={subscriptionOverview.url}
                                />
                                <div className="control-center__layout__status">
                                    <StatusControlCenterTile
                                        status={subscriptionOverview.status}
                                        licenseExpired={subscriptionOverview.licenseExpired}
                                        resourcesProvisioned={subscriptionOverview.areResourcesProvisioned}
                                    />
                                    <ValidUntilControlCenterTile
                                        validUntil={subscriptionOverview.validUntil}
                                        licenseExpired={subscriptionOverview.licenseExpired}
                                        subscriptionState={subscriptionOverview.state}
                                    />
                                </div>
                                <UsersControlCenterTile
                                    subscriptionId={subscriptionOverview.id}
                                    plans={subscriptionOverview.plans}
                                    licensedUserCount={subscriptionOverview.minimumLicensesNeededInfo?.licensedUserCount}
                                    type={subscriptionOverview.type}
                                    onViewLicensesClicked={onViewLicensesClicked}
                                />
                                <PurchasedPlanControlCenterTile
                                    plans={subscriptionOverview.plans}
                                    type={subscriptionOverview.type}
                                    onBuyNowClicked={onBuyNowClicked}
                                />
                            </div>
                        </div>

                        <div className="control-center__footer">
                            {subscriptionOverview.type == SubscriptionTypeEnum.Cloud &&
                                <Button
                                    className="open-point"
                                    disabled={subscriptionOverview && !subscriptionOverview.areResourcesProvisioned}
                                    onClick={onOpenPointClickCallback}
                                    secondary
                                >
                                    Open Syskit Point
                                </Button>
                            }
                            {
                                subscriptionOverview.type == SubscriptionTypeEnum.Enterprise &&
                                <Button
                                    className="control-center__footer__copy-license"
                                    onClick={onCopyLicenseKeyClickCallback}
                                    loading={copyLicenseKeyState === CopyLicenseKeyState.Getting}
                                    disabled={copyLicenseKeyState === CopyLicenseKeyState.Getting}
                                    icon={IconEnum.Clone}
                                    secondary
                                >
                                    {copyLicenseKeyState === CopyLicenseKeyState.Copied ? 'Copied!' :
                                        copyLicenseKeyState === CopyLicenseKeyState.Getting ? 'Getting key...' :
                                            copyLicenseKeyState === CopyLicenseKeyState.Error ? 'Copy failed. Try again.' :
                                            'Copy License Key'}
                                </Button>
                            }

                            {subscriptionOverview && subscriptionOverview.licenseExpired &&
                                <Button onClick={onContactSupportClicked}>
                                    Contact Support
                                </Button>
                            }
                        </div>
                    </>)}
            </div>
        </div>
    );
};


export default controlCenter;
